
import React, { useState } from 'react';
import { User, TotpMethod } from '../types';
import { api } from '../services/apiService';
import { MOCK_USERS } from '../constants';
import { validatePassword, PASSWORD_HINT } from '../utils/passwordValidation';
import { toast } from '../services/toast';

interface LoginProps {
  onLogin: (user: User) => Promise<void> | void;
}

const API_BASE = (import.meta.env.VITE_API_URL as string) || '/api/index.php';

const Login: React.FC<LoginProps> = ({ onLogin }) => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  // Reset password flow
  const [resetMode, setResetMode] = useState<'login' | 'forgot' | 'code' | 'totp'>('login');
  const [resetEmail, setResetEmail] = useState('');
  const [resetCode, setResetCode] = useState('');
  const [resetNewPassword, setResetNewPassword] = useState('');
  const [resetLoading, setResetLoading] = useState(false);

  // 2FA — étape 2 de connexion si l'user a activé le 2FA (TOTP app ou email)
  const [pendingToken, setPendingToken] = useState<string | null>(null);
  const [totpCode, setTotpCode] = useState('');
  const [totpLoading, setTotpLoading] = useState(false);
  const [totpMethod, setTotpMethod] = useState<TotpMethod>('app');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      const result = await api.login(email, password);

      if (!result) {
        setError("Identifiants incorrects.");
        setLoading(false);
        return;
      }
      if (result.kind === 'totp_required') {
        setPendingToken(result.pendingToken);
        setTotpMethod(result.totpMethod);
        setResetMode('totp');
        setLoading(false);
        return;
      }
      // result.kind === 'success'
      await onLogin(result.user);
    } catch (err: any) {
      setError(err?.message || "Erreur technique. Réessayez.");
      setLoading(false);
    }
  };

  const handleTotpSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!pendingToken) return;
    const trimmed = totpCode.trim();
    if (!trimmed) return;
    setTotpLoading(true);
    setError('');
    try {
      const user = totpMethod === 'email'
        ? await api.loginEmailOtp(pendingToken, trimmed)
        : await api.loginTotp(pendingToken, trimmed);
      await onLogin(user);
    } catch (err: any) {
      setError(err?.message || 'Code invalide.');
      setTotpLoading(false);
    }
  };

  const cancelTotp = () => {
    setPendingToken(null);
    setTotpCode('');
    setError('');
    setResetMode('login');
  };

  const handleForgotPassword = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!resetEmail.trim()) return;
    setResetLoading(true);
    try {
      const res = await fetch(`${API_BASE}?action=forgot_password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: resetEmail })
      });
      const data = await res.json();
      if (data.success) {
        toast.info('Si ce compte existe, un code à 6 chiffres a été envoyé par email.', { duration: 8000 });
        setResetMode('code');
      } else {
        toast.error(data.error || 'Erreur');
      }
    } catch {
      toast.error('Erreur technique.');
    } finally {
      setResetLoading(false);
    }
  };

  const handleResetPassword = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!resetCode.trim() || !resetNewPassword.trim()) return;
    const pwError = validatePassword(resetNewPassword);
    if (pwError) { toast.error(pwError); return; }
    setResetLoading(true);
    try {
      const res = await fetch(`${API_BASE}?action=reset_password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token: resetCode, password: resetNewPassword })
      });
      const data = await res.json();
      if (data.success) {
        toast.success('Mot de passe modifié ! Vous pouvez vous connecter.');
        setTimeout(() => {
          setResetMode('login');
          setResetCode('');
          setResetNewPassword('');
        }, 2000);
      } else {
        toast.error(data.error || 'Code invalide ou expiré.');
      }
    } catch {
      toast.error('Erreur technique.');
    } finally {
      setResetLoading(false);
    }
  };

  const quickFill = (u: typeof MOCK_USERS[0]) => {
      setEmail(u.email);
      setPassword(u.password || '123');
  };

  return (
    <div className="orion-login-page min-h-screen flex items-center justify-center p-6 relative overflow-hidden">
      {/* Animated background orbs */}
      <div className="orion-login-orb orion-login-orb-1" />
      <div className="orion-login-orb orion-login-orb-2" />
      <div className="orion-login-orb orion-login-orb-3" />

      <div className="orion-login-card w-full max-w-[420px] relative z-10">
        {/* Logo section */}
        <div className="flex flex-col items-center pt-10 pb-6 px-8">
          <img
            src="/images/logo-orion.png"
            alt="ORION CRM"
            className="h-14 w-auto object-contain mb-6 drop-shadow-[0_0_24px_rgba(17,197,214,0.25)]"
          />
          <p className="text-sm font-medium text-[#7f93bc] tracking-wide">
            {resetMode === 'login' ? 'Connectez-vous pour continuer' :
             resetMode === 'forgot' ? 'Réinitialiser votre mot de passe' :
             'Entrez le code reçu par email'}
          </p>
        </div>

        {/* Separator */}
        <div className="mx-8 h-px bg-gradient-to-r from-transparent via-[rgba(100,140,210,0.3)] to-transparent" />

        {/* Form section */}
        <div className="p-8">
          {/* ========== LOGIN FORM ========== */}
          {resetMode === 'login' && (
            <>
              <form onSubmit={handleSubmit} className="space-y-5">
                {error && (
                  <div className="flex items-center gap-2.5 p-3.5 rounded-xl border border-rose-500/30 bg-rose-500/10 animate-in fade-in">
                    <svg className="w-4 h-4 text-rose-400 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4.5c-.77-.833-2.694-.833-3.464 0L3.34 16.5c-.77.833.192 2.5 1.732 2.5z" />
                    </svg>
                    <span className="text-xs font-bold text-rose-300">{error}</span>
                  </div>
                )}

                <div className="space-y-1.5">
                  <label className="text-[10px] font-black text-[#7f93bc] uppercase tracking-[0.15em] ml-1">Email professionnel</label>
                  <div className="relative">
                    <div className="absolute left-3.5 top-1/2 -translate-y-1/2 text-[#546a96]">
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                      </svg>
                    </div>
                    <input
                      type="email"
                      required
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      className="orion-login-input w-full pl-10 pr-4 py-3.5 rounded-xl text-sm font-semibold"
                      placeholder="votre@email.com"
                    />
                  </div>
                </div>

                <div className="space-y-1.5">
                  <label className="text-[10px] font-black text-[#7f93bc] uppercase tracking-[0.15em] ml-1">Mot de passe</label>
                  <div className="relative">
                    <div className="absolute left-3.5 top-1/2 -translate-y-1/2 text-[#546a96]">
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                      </svg>
                    </div>
                    <input
                      type={showPassword ? 'text' : 'password'}
                      required
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      className="orion-login-input w-full pl-10 pr-11 py-3.5 rounded-xl text-sm font-semibold"
                      placeholder="••••••••"
                    />
                    <button
                      type="button"
                      title={showPassword ? 'Masquer le mot de passe' : 'Afficher le mot de passe'}
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-[#546a96] hover:text-[#7f93bc] transition-colors"
                    >
                      {showPassword ? (
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.878 9.878L3 3m6.878 6.878L21 21" /></svg>
                      ) : (
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" /><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" /></svg>
                      )}
                    </button>
                  </div>
                </div>

                <button
                  type="submit"
                  disabled={loading}
                  className="orion-login-submit w-full py-4 rounded-xl text-xs font-black uppercase tracking-[0.2em] flex items-center justify-center gap-2.5 mt-2 disabled:opacity-60"
                >
                  {loading ? (
                    <>
                      <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none"></circle><path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path></svg>
                      Connexion...
                    </>
                  ) : (
                    <>
                      Se connecter
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M13 7l5 5m0 0l-5 5m5-5H6" />
                      </svg>
                    </>
                  )}
                </button>
              </form>

              <div className="mt-4 text-center">
                <button
                  type="button"
                  onClick={() => { setResetMode('forgot'); setResetEmail(email); }}
                  className="text-[11px] font-bold text-[#546a96] hover:text-[#11c5d6] transition-colors"
                >
                  Mot de passe oublié ?
                </button>
              </div>
            </>
          )}

          {/* ========== FORGOT PASSWORD FORM ========== */}
          {resetMode === 'forgot' && (
            <form onSubmit={handleForgotPassword} className="space-y-5">
              <div className="space-y-1.5">
                <label className="text-[10px] font-black text-[#7f93bc] uppercase tracking-[0.15em] ml-1">Votre email</label>
                <input
                  type="email"
                  required
                  value={resetEmail}
                  onChange={(e) => setResetEmail(e.target.value)}
                  className="orion-login-input w-full px-4 py-3.5 rounded-xl text-sm font-semibold"
                  placeholder="votre@email.com"
                />
              </div>
              <button
                type="submit"
                disabled={resetLoading}
                className="orion-login-submit w-full py-4 rounded-xl text-xs font-black uppercase tracking-[0.2em] flex items-center justify-center gap-2 disabled:opacity-60"
              >
                {resetLoading ? 'Envoi...' : 'Envoyer le code'}
              </button>
              <div className="text-center">
                <button type="button" onClick={() => { setResetMode('login'); }} className="text-[11px] font-bold text-[#546a96] hover:text-[#11c5d6] transition-colors">
                  Retour a la connexion
                </button>
              </div>
            </form>
          )}

          {/* ========== RESET CODE + NEW PASSWORD ========== */}
          {resetMode === 'code' && (
            <form onSubmit={handleResetPassword} className="space-y-5">
              <div className="space-y-1.5">
                <label className="text-[10px] font-black text-[#7f93bc] uppercase tracking-[0.15em] ml-1">Code à 6 chiffres reçu par email</label>
                <input
                  type="text"
                  inputMode="numeric"
                  pattern="[0-9]{6}"
                  required
                  value={resetCode}
                  onChange={(e) => setResetCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                  className="orion-login-input w-full px-4 py-3.5 rounded-xl text-2xl font-black tracking-[0.5em] text-center"
                  placeholder="000000"
                  maxLength={6}
                />
              </div>
              <div className="space-y-1.5">
                <label className="text-[10px] font-black text-[#7f93bc] uppercase tracking-[0.15em] ml-1">Nouveau mot de passe</label>
                <div className="relative">
                  <input
                    type={showPassword ? 'text' : 'password'}
                    required
                    value={resetNewPassword}
                    onChange={(e) => setResetNewPassword(e.target.value)}
                    className="orion-login-input w-full px-4 pr-11 py-3.5 rounded-xl text-sm font-semibold"
                    placeholder="••••••••"
                    minLength={12}
                  />
                  <p className="text-[10px] text-[#546a96] mt-1">{PASSWORD_HINT}</p>
                  <button
                    type="button"
                    title={showPassword ? 'Masquer' : 'Afficher'}
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-[#546a96] hover:text-[#7f93bc] transition-colors"
                  >
                    {showPassword ? (
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.878 9.878L3 3m6.878 6.878L21 21" /></svg>
                    ) : (
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" /><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" /></svg>
                    )}
                  </button>
                </div>
              </div>
              <button
                type="submit"
                disabled={resetLoading}
                className="orion-login-submit w-full py-4 rounded-xl text-xs font-black uppercase tracking-[0.2em] flex items-center justify-center gap-2 disabled:opacity-60"
              >
                {resetLoading ? 'Modification...' : 'Changer le mot de passe'}
              </button>
              <div className="text-center">
                <button type="button" onClick={() => { setResetMode('login'); }} className="text-[11px] font-bold text-[#546a96] hover:text-[#11c5d6] transition-colors">
                  Retour a la connexion
                </button>
              </div>
            </form>
          )}

          {/* ========== TOTP 2FA — étape 2 de la connexion ========== */}
          {resetMode === 'totp' && (
            <form onSubmit={handleTotpSubmit} className="space-y-5">
              {error && (
                <div className="flex items-center gap-2.5 p-3.5 rounded-xl border border-rose-500/30 bg-rose-500/10 animate-in fade-in">
                  <span className="text-xs font-bold text-rose-300">{error}</span>
                </div>
              )}
              <div className="space-y-1.5">
                <label className="text-[10px] font-black text-[#7f93bc] uppercase tracking-[0.15em] ml-1">
                  {totpMethod === 'email'
                    ? 'Code à 6 chiffres reçu par email'
                    : 'Code à 6 chiffres de votre application 2FA'}
                </label>
                <input
                  type="text"
                  inputMode="numeric"
                  required
                  autoFocus
                  value={totpCode}
                  onChange={(e) => totpMethod === 'email'
                    ? setTotpCode(e.target.value.replace(/\D/g, '').slice(0, 6))
                    : setTotpCode(e.target.value.replace(/[^\d\-A-Za-z]/g, '').slice(0, 19))}
                  className="orion-login-input w-full px-4 py-3.5 rounded-xl text-2xl font-black tracking-[0.5em] text-center"
                  placeholder="000000"
                  maxLength={totpMethod === 'email' ? 6 : undefined}
                />
                <p className="text-[10px] text-[#546a96] mt-1">
                  {totpMethod === 'email'
                    ? "Le code expire dans 5 minutes. Pensez à vérifier vos spams si vous ne le recevez pas."
                    : "Vous pouvez aussi saisir un code de secours (16 caractères) si vous n'avez pas accès à votre application."}
                </p>
              </div>
              <button
                type="submit"
                disabled={totpLoading || !totpCode.trim()}
                className="orion-login-submit w-full py-4 rounded-xl text-xs font-black uppercase tracking-[0.2em] flex items-center justify-center gap-2 disabled:opacity-60"
              >
                {totpLoading ? 'Verification...' : 'Verifier le code'}
              </button>
              <div className="text-center">
                <button type="button" onClick={cancelTotp} className="text-[11px] font-bold text-[#546a96] hover:text-[#11c5d6] transition-colors">
                  Annuler
                </button>
              </div>
            </form>
          )}

        </div>

        {/* Footer */}
        <div className="pb-6 text-center space-y-2">
          <span className="text-[10px] text-[#3d5280] font-medium">ORION CRM v2.0</span>
          <div>
            <a
              href="/politique-de-confidentialite"
              target="_blank"
              rel="noopener noreferrer"
              className="text-[10px] text-[#546a96] hover:text-[#11c5d6] transition-colors"
            >
              Politique de confidentialité
            </a>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Login;
